// CUBBO-2440: re-entering a terminal order (shipped/cancelled) in the packing
// beta view used to hard-stop the session peek, so the packed counts showed
// 0/N and the already-generated cajas/documents never rendered. These pure
// helpers drive the read-only restore: which session to attach for display,
// and whether to keep polling.

// Terminal = the order is done packing. We attach its session read-only
// (display counts + cajas/docs) but never reopen the packing cycle.
export const isTerminalOrderStatus = (status) =>
    status === 'shipped' || status === 'cancelled'

// Which packing session to load into the ['packingSession', id] query.
//   - Active order  → the OPEN session (resume in-progress packing).
//   - Terminal order → the most-recent session by created_at. That's the one
//     that produced the shipment; its packing_boxes carry the packed_units and
//     document URLs we restore in read-only.
// Returns null when there's nothing to attach.
export const pickSessionForDisplay = (sessions, orderStatus) => {
    const list = sessions || []
    if (!list.length) return null

    if (isTerminalOrderStatus(orderStatus)) {
        // Coerce created_at to a timestamp, treating missing/malformed values as
        // oldest (-Infinity) so a session we can't date never shadows a real one.
        // Never subtract two possibly-NaN dates directly — that yields NaN and an
        // unreliable sort that could restore the wrong (e.g. empty) session.
        const toTime = (s) => {
            const t = new Date(s?.created_at).getTime()
            return Number.isNaN(t) ? -Infinity : t
        }
        // slice() so we never mutate the caller's array while sorting.
        return list.slice().sort((a, b) => {
            const ta = toTime(a)
            const tb = toTime(b)
            // Most-recent first; tie-break on id (the higher id is the later-created
            // row) so ties / all-undated lists stay deterministic.
            if (tb !== ta) return tb - ta
            return (b.id || 0) - (a.id || 0)
        })[0]
    }

    return list.find((s) => s.status === 'OPEN') || null
}

// react-query refetchInterval value for the packing session.
//   - Terminal order → false: the read-only view has nothing to sync, so we
//     don't hammer GET /packing-sessions/:id every 3s on a shipped order.
//   - CLOSED session → false: nothing more will change.
//   - several ACTIVE participants → 3000ms: the poll is how one station sees
//     the other's scans (CUBBO-2067).
//   - one (or zero) active participants → 12000ms: their own scans patch the
//     cache directly, so the poll only reconciles drift — no reason for every
//     open station to make the backend re-render a big session every 3s.
//   - participant count still unknown (first tick) → 3000ms until the session
//     payload says.
export const shouldPollPackingSession = (orderStatus, sessionStatus, participantsCount = null) => {
    if (isTerminalOrderStatus(orderStatus)) return false
    if (sessionStatus === 'CLOSED') return false
    if (participantsCount == null) return 3000
    return participantsCount > 1 ? 3000 : 12000
}

// A participant idle for this long is no longer a reason to poll fast. Their
// last_active_at is touched by every scan/close they make, so an actually
// co-packing station refreshes it constantly; 5 minutes tolerates a tote
// change or a bathroom break without flapping.
export const PARTICIPANT_ACTIVITY_WINDOW_MS = 5 * 60 * 1000

// How many participants were seen recently. PackingSession#participants is
// everyone who has EVER joined (has_many :through) — after any "cambiar de
// usuario" handoff on a shared station its raw length is permanently > 1, so
// counting rows meant those sessions never relaxed the poll. Null when the
// payload hasn't said yet (shouldPollPackingSession treats unknown as "poll
// fast"). A participant without last_active_at counts by joined_at; one with
// no usable date at all is not counted rather than pinning the fast poll on.
export const countActiveParticipants = (participants, nowMs = Date.now(), windowMs = PARTICIPANT_ACTIVITY_WINDOW_MS) => {
    if (participants == null) return null
    return participants.filter(p => {
        const seenAt = new Date(p?.last_active_at ?? p?.joined_at ?? NaN).getTime()
        return !Number.isNaN(seenAt) && nowMs - seenAt <= windowMs
    }).length
}

// Guards the multi-box "lazy open" lifecycle so at most ONE `POST /boxes` is in
// flight per next-slot — StrictMode's dev double-invoke and rapid dependency
// changes would otherwise fire two opens before the first is reflected in the
// cache. Crucially the guard is IN-FLIGHT-ONLY: `release` frees the slot once its
// open settles, so a box that is later closed or deleted can be opened again.
//
// A permanent "already opened" history was the CUBBO-2440 (d) bug: `existing`
// (the live-box count) repeats as boxes are closed/deleted and operators reuse
// the same empaque, so the `${existing}-${packageId}` key collided and the POST
// was silently skipped — no backend box was created, leaving a phantom card that
// blocked scanning and wrongly enabled the global-packing-list footer.
export const createBoxOpenGuard = () => {
    const inFlight = new Set()
    return {
        // Claims the next slot that still needs a backend box. Returns
        // { positionKey, shippingPackageId } and marks it in-flight, or null when
        // the next slot has no empaque picked yet, or an open for that exact slot
        // is already in flight.
        claim(liveBoxCount, positions) {
            const pkg = (positions || [])[liveBoxCount]
            if (!pkg || !pkg.id) return null
            const positionKey = `${liveBoxCount}-${pkg.id}`
            if (inFlight.has(positionKey)) return null
            inFlight.add(positionKey)
            return { positionKey, shippingPackageId: pkg.id }
        },
        // Frees a slot once its open settles (success OR failure). Idempotent.
        release(positionKey) {
            inFlight.delete(positionKey)
        },
        // Drops every in-flight mark — used when the packing session changes.
        reset() {
            inFlight.clear()
        },
    }
}

// The barcode scanner is a keyboard: it types into whatever element has the
// focus. Rule for the multi-box screen — an OPEN caja WITH its empaque picked
// owns the scan input; a caja still waiting for its empaque leaves the focus in
// the empaque combobox (which autofocuses itself); an open dialog (packing-list
// preview, IMEI, unit code, unknown-code warning) is never fought. Before this
// rule nobody handed the focus back after the operator picked the empaque
// (Headless UI re-focuses its own combobox input), so the next scan typed into
// the empaque filter and the operator refreshed the page to recover.
// Accepts both box shapes in play: the UI-mapped one (`is_closed` +
// `shipping_package_id`) and the raw backend one (`status` + `shipping_package`).
export const shouldFocusScanInput = ({ multiBox, readOnly, activeBox, dialogOpen }) => {
    if (!multiBox || readOnly || dialogOpen) return false
    if (!activeBox) return false
    if (activeBox.is_closed === true || activeBox.status === 'closed') return false
    const packageId = activeBox.shipping_package_id ?? activeBox.shipping_package?.id ?? null
    return packageId != null
}

// Serializes the per-unit /scan POSTs of ONE caja on the client. The backend
// takes a row lock per box, so N POSTs fired at once (the pending-scan drain,
// a fast burst) queue behind each other server-side and the last one waits
// N × ~100ms — 20 scans ≈ 3.5s, 57 ≈ 8.5s, more and Rack::Timeout kills them.
// One request in flight per box keeps every scan fast, keeps the responses in
// FIFO order, and never lets one caja's queue delay another's. A rejected task
// (422) does not block the tasks queued behind it.
export const createBoxScanQueue = () => {
    const tails = new Map()
    return {
        enqueue(boxId, task) {
            const previous = tails.get(boxId) || Promise.resolve()
            const run = previous.then(() => task(), () => task())
            tails.set(boxId, run)
            const settle = () => {
                if (tails.get(boxId) === run) tails.delete(boxId)
            }
            run.then(settle, settle)
            return run
        },
    }
}

// Which caja the next scans go to, after the session's boxes changed.
//   - `preferredBoxId` (the caja the operator just opened by picking its empaque
//     on a "+ Añadir otra caja" slot) wins as soon as it is present and open:
//     that is where they are about to scan, and switching to it also hands the
//     focus back to the scan input. Before, the active caja stayed on the first
//     open one, the new card lost its highlight the moment it became real, and a
//     scan would have landed in the OLD caja.
//   - otherwise the current active caja stays while it is open;
//   - otherwise the first open caja, or null when none is open.
// Accepts the UI-mapped box shape (`is_closed`).
export const resolveActiveBoxId = ({ boxes, activeBoxId, preferredBoxId = null }) => {
    const list = boxes || []
    const preferred = preferredBoxId != null ? list.find(b => b.id === preferredBoxId) : null
    if (preferred && !preferred.is_closed) return preferred.id
    const current = list.find(b => b.id === activeBoxId)
    if (current && !current.is_closed) return current.id
    return list.find(b => !b.is_closed)?.id ?? null
}

// A scan whose response never came back (network error / client timeout) or
// died server-side (5xx: Rack::Timeout, Heroku H12) MAY still have been
// committed before the response was lost. Rolling the optimistic count back
// blindly makes the operator rescan the piece — and the rescan packs a SECOND
// unit. Those get reconciled against a fresh session read; a 4xx is a final
// "no" and rolls back right away.
export const isTransientScanError = (err) => {
    const status = err?.response?.status
    if (status == null) return true
    return status >= 500
}

// Settles a scan attempt by IDENTITY, never by comparing box totals. `attempt`
// must re-POST the SAME scan_key each time it is called: the backend then
// answers for this exact attempt — already packed (response lost mid-flight) →
// the same unit comes back, replayed; never arrived → it packs now. One replay
// only: if that also fails, the caller rolls back and shows the error — an
// unnecessary rescan is recoverable, a silently-confirmed missing piece is not.
// A 4xx is a final answer either way and is never replayed. Count-threshold
// reconciles were retired here: a count snapshot is shared by every scan queued
// in a burst (false confirm) and a fresh read can be answered by an in-flight
// poll that predates the scan (false rollback → duplicate piece).
export const scanWithReplay = async (attempt, isTransient = isTransientScanError) => {
    try {
        return await attempt()
    } catch (err) {
        if (!isTransient(err)) throw err
        return await attempt()
    }
}

// Merges a /scan response into the cached session. Two shapes are in play:
//   - slim ({ box, packed_units_added, packed_units_count }): the box header is
//     merged and only the units THIS scan added are APPENDED — the backend no
//     longer hauls every packed unit on every scan (~230B/unit; O(n) per scan).
//     A replayed unit already present (scan_key retry) is not appended twice.
//   - legacy (the full box): replaced wholesale, kept for the deploy window
//     where platform still answers with it.
export const applyScanResponse = (session, response) => {
    if (!session || !response) return session
    const slim = !!response.box
    const updatedBox = slim ? response.box : response
    return {
        ...session,
        packing_boxes: (session.packing_boxes || []).map(box => {
            if (box.id !== updatedBox.id) return box
            if (!slim) return { ...box, ...updatedBox }
            const existing = box.packed_units || []
            const added = (response.packed_units_added || []).filter(unit => !existing.some(e => e.id === unit.id))
            return { ...box, ...updatedBox, packed_units: [...existing, ...added] }
        }),
    }
}

// A "junk" caja: closed with nothing packed in it — the clean-up artifact left
// behind when an empty box is closed or auto-closed. These are hidden from the
// operator and excluded from every derived count so the panel cards, the
// handlers' indices, the "Paquete N" numbering and the lazy-open's next-slot
// position all stay compact and agree with each other. Leaving holes in those
// sequences made a virtual slot land at an index the lazy-open didn't
// recognise, so the next caja never opened and close/delete hit the wrong box.
//
// `orderHasNoScannableItems` is the ESCAPE HATCH, and it is not optional: when
// the order carries nothing scannable (inserts / marketing material / any SKU
// flagged not-scannable), there is nothing to bip, so a caja the operator
// legitimately sealed ALSO has zero packed units. "Closed + empty" then stops
// being evidence of junk, and treating it as junk hid every real caja — the
// panel reported "0 cerradas", the footer's all-boxes-closed gate never passed,
// and the restore snapshot produced no packages, so "Generar etiquetas de
// envío" stayed disabled forever while each click sealed (and billed) another
// caja. Callers on such an order MUST pass true.
//
// Accepts both box shapes in play: the UI-mapped box (`is_closed` + `items`
// keyed by SKU) and the raw backend box (`status` + `packed_units`).
//
// CALL SITES — every one of them must come through here, because a copy that
// drifts is exactly how the earlier fix rotted (PR #507 wired the detector to
// ONE site and left four inline copies, so closing the caja was enabled while
// seeing it was not):
//   GenerateShipment.js  sessionBoxes filter, isBoxDiscarded (Paquete N
//                        numbering), the restore effect, the lazy-open effect
//   MultiBoxPackingPanel render skip (the "N cerradas" count)
//   labelPackages.js     shippingUnitsOf — the packages quoted to the carrier
export const isJunkBox = (box, orderHasNoScannableItems = false) => {
    if (!box) return false
    if (orderHasNoScannableItems) return false
    const closed = box.is_closed ?? box.status === 'closed'
    if (!closed) return false
    // Prefer `items` when the caller mapped it (a mapped box always has the key,
    // even when empty); fall back to the raw packed_units array.
    const packedCount = box.items
        ? Object.keys(box.items).length
        : (box.packed_units || []).length
    return packedCount === 0
}
